import { create } from "zustand";
import {
  DeliverableBranding,
  DeliverableBuildSource,
  DeliverableFocalContextInput,
  DeliverableSourceInput,
  InspectSourceResult,
  PrepareLogoResult,
} from "@/types";

export type DeliverableSourceStatus =
  "inspecting" | "ready" | "rejected" | "error";

export interface DeliverableSourceFile {
  localId: string;
  file: File;
  status: DeliverableSourceStatus;
  include: boolean;
  pages?: string;
  inspection?: InspectSourceResult;
  errorMessage?: string;
}

/**
 * Display text for a selection, cached by id: a picked row leaves the search
 * results as soon as the term changes, and its chip still has to render.
 */
export interface ContextLabel {
  title: string;
  subtitle: string;
}

interface DeliverableCompositionState {
  name: string;
  promptText: string;
  /**
   * What Focal should emphasise or leave out. Its own field, never folded into
   * `promptText`: the build prompts read that as the document's section
   * contract, so an exclusion inside it is read as a section instruction.
   */
  focusInstructions: string;
  /** All selected clients contribute opted-in data. Maximum 10. */
  clientIds: string[];
  templateName: string | null;
  /** Null for the three built-in defaults, which are client-side constants with no stored row. */
  templateId: string | null;
  /** The Focal records picked as extra context; sent as `focalContext`. */
  conversationIds: string[];
  crmNoteIds: string[];
  /** Opt-in data from every selected client. */
  includeCrmHousehold: boolean;
  includeClientData: boolean;
  contextLabels: Record<string, ContextLabel>;
  noteClientIds: Record<string, string[]>;
  rememberNoteClients: (associations: Record<string, string[]>) => void;
  userBrandColorOverrides: Partial<
    Record<keyof DeliverableBranding["colors"], boolean>
  >;
  branding: DeliverableBranding;
  sources: DeliverableSourceFile[];
  logoFile: File | null;
  logo: PrepareLogoResult | null;
  defaultLogoFileName: string | null;
  defaultLogoDismissed: boolean;

  setName: (value: string) => void;
  setPromptText: (value: string) => void;
  setFocusInstructions: (value: string) => void;
  /** Replaces the whole client selection, in pick order. */
  setClientIds: (value: string[]) => void;
  /** The client the per-client snapshots and the create payload belong to. */
  primaryClientId: () => string | null;
  /** Sets both together, or clears both with `null` — a half-set pair would put a pill on a deliverable that no template built. */
  setTemplate: (template: { name: string; id: string | null } | null) => void;
  /** Replaces the whole selection, in pick order — the order sent on the wire. */
  setConversationIds: (value: string[]) => void;
  setCrmNoteIds: (value: string[]) => void;
  setIncludeCrmHousehold: (value: boolean) => void;
  setIncludeClientData: (value: boolean) => void;
  rememberContextLabels: (labels: Record<string, ContextLabel>) => void;
  setBrandColor: (
    role: keyof DeliverableBranding["colors"],
    value: string,
  ) => void;
  setBrandColors: (colors: DeliverableBranding["colors"]) => void;
  addSources: (sources: DeliverableSourceFile[]) => void;
  updateSource: (
    localId: string,
    patch: Partial<Omit<DeliverableSourceFile, "localId" | "file">>,
  ) => void;
  removeSource: (localId: string) => void;
  clearSources: () => void;
  setLogoFile: (value: File | null) => void;
  setLogo: (value: PrepareLogoResult | null) => void;
  setDefaultLogoFileName: (value: string | null) => void;
  setDefaultLogoDismissed: (value: boolean) => void;
  includedSources: () => DeliverableBuildSource[];
  /**
   * The advisor's own uploads, as the monolith stores them. Focal records are
   * deliberately absent: the picked ids travel as `focalContext` on the create
   * call, and the monolith resolves them into corpus rows there — on the
   * advisor's own token, which the later server-owned build no longer has.
   * Resolving them here would mean re-reading Focal in the browser only to hand
   * the server back what it is about to read anyway.
   */
  persistableSources: () => DeliverableSourceInput[];
  /**
   * `null` when nothing is picked — `BuildRequest` is `extra="forbid"`, so the
   * key must be omitted rather than sent empty. Needs no client: conversations
   * are searched firm-wide. The client flags count only while a client is
   * attached, which `setClientIds` already guarantees by clearing them.
   */
  focalContext: () => DeliverableFocalContextInput | null;
  reset: () => void;
}

const INITIAL_STATE = {
  name: "",
  promptText: "",
  focusInstructions: "",
  clientIds: [] as string[],
  templateName: null as string | null,
  templateId: null as string | null,
  conversationIds: [] as string[],
  crmNoteIds: [] as string[],
  includeCrmHousehold: false,
  includeClientData: false,
  contextLabels: {} as Record<string, ContextLabel>,
  noteClientIds: {} as Record<string, string[]>,
  userBrandColorOverrides: {},
  branding: { colors: {} } as DeliverableBranding,
  sources: [] as DeliverableSourceFile[],
  logoFile: null,
  logo: null,
  defaultLogoFileName: null,
  defaultLogoDismissed: false,
};

export const useDeliverableCompositionStore =
  create<DeliverableCompositionState>((set, get) => ({
    ...INITIAL_STATE,
    setName: (name) => set({ name }),
    setPromptText: (promptText) => set({ promptText }),
    setFocusInstructions: (focusInstructions) => set({ focusInstructions }),
    setClientIds: (value) =>
      set((state) => {
        const clientIds = [...new Set(value)];
        if (clientIds.length > 10) return state;
        return {
          clientIds,
          crmNoteIds: state.crmNoteIds.filter((id) =>
            (state.noteClientIds[id] ?? state.clientIds).some((clientId) =>
              clientIds.includes(clientId),
            ),
          ),
          ...(clientIds.length === 0
            ? { includeCrmHousehold: false, includeClientData: false }
            : {}),
        };
      }),
    rememberNoteClients: (associations) =>
      set((state) => ({
        noteClientIds: Object.fromEntries(
          Object.entries({ ...state.noteClientIds, ...associations }).map(
            ([id, clients]) => [
              id,
              [...new Set([...(state.noteClientIds[id] ?? []), ...clients])],
            ],
          ),
        ),
      })),
    primaryClientId: () => get().clientIds[0] ?? null,
    setTemplate: (template) =>
      set(
        template
          ? { templateName: template.name, templateId: template.id }
          : { templateName: null, templateId: null },
      ),
    setConversationIds: (conversationIds) => set({ conversationIds }),
    setCrmNoteIds: (crmNoteIds) => set({ crmNoteIds }),
    setIncludeCrmHousehold: (includeCrmHousehold) =>
      set({ includeCrmHousehold }),
    setIncludeClientData: (includeClientData) => set({ includeClientData }),
    rememberContextLabels: (labels) =>
      set((state) => ({
        contextLabels: { ...state.contextLabels, ...labels },
      })),
    setBrandColor: (role, value) =>
      set((state) => ({
        userBrandColorOverrides: {
          ...state.userBrandColorOverrides,
          [role]: true,
        },
        branding: {
          colors: { ...state.branding.colors, [role]: value || undefined },
        },
      })),
    setBrandColors: (colors) => set({ branding: { colors: { ...colors } } }),
    addSources: (sources) =>
      set((state) => ({ sources: [...state.sources, ...sources] })),
    updateSource: (localId, patch) =>
      set((state) => ({
        sources: state.sources.map((source) =>
          source.localId === localId ? { ...source, ...patch } : source,
        ),
      })),
    removeSource: (localId) =>
      set((state) => ({
        sources: state.sources.filter((source) => source.localId !== localId),
      })),
    clearSources: () => set({ sources: [] }),
    setLogoFile: (logoFile) => set({ logoFile }),
    setLogo: (logo) => set({ logo }),
    setDefaultLogoFileName: (defaultLogoFileName) =>
      set({ defaultLogoFileName }),
    setDefaultLogoDismissed: (defaultLogoDismissed) =>
      set({ defaultLogoDismissed }),
    includedSources: () =>
      get()
        .sources.filter((source) => source.include && source.status === "ready")
        .map(({ file, inspection }) => ({
          filename: inspection?.filename ?? file.name,
          mimeType: inspection?.mimeType ?? file.type,
          extractedText: inspection?.extractedText ?? "",
          pageCount: inspection?.pageCount,
          sheetCount: inspection?.sheetCount,
          estimatedTokens: inspection?.estimatedTokens,
        })),
    persistableSources: () =>
      get()
        .sources.filter(
          (source) => source.status === "ready" || source.status === "rejected",
        )
        .map(({ file, inspection, include, status }, order) => ({
          filename: inspection?.filename ?? file.name,
          mimeType: inspection?.mimeType ?? file.type,
          extractedText: inspection?.extractedText ?? "",
          ok: status === "ready",
          rejectionReason: inspection?.reason,
          pageCount: inspection?.pageCount,
          sheetCount: inspection?.sheetCount,
          estimatedTokens: inspection?.estimatedTokens,
          includedInBuild: include,
          origin: "BUILD" as const,
          order,
        })),
    focalContext: () => {
      const {
        conversationIds,
        crmNoteIds,
        includeCrmHousehold,
        includeClientData,
      } = get();
      if (
        conversationIds.length === 0 &&
        crmNoteIds.length === 0 &&
        !includeCrmHousehold &&
        !includeClientData
      ) {
        return null;
      }
      // No `clientIds` here on purpose: the attached clients are a top-level
      // field on the create payload, and the server's focalContext DTO rejects
      // any property it does not whitelist.
      return {
        conversationIds,
        crmNoteIds,
        includeCrmHousehold,
        includeClientData,
      };
    },
    reset: () => set({ ...INITIAL_STATE }),
  }));
